export const FT2_PER_M2 = 10.7639;
export const LITRES_PER_US_GALLON = 3.78541;
export const MICRONS_PER_MIL = 25.4;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundValue = (value, digits = 4) => {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(digits));
};

export const deriveCoatingCalculations = (data, editedUnit = {}) => {
  const mode = data.input_uom_mode || "imperial";
  const preferredFor = (key) => editedUnit[key] || mode;

  let theoreticalFt2PerGal = toNumber(data.coverage_ft2_per_gal);
  let theoreticalM2PerL = toNumber(data.coverage_25um_m2_per_l);
  let wftMils = toNumber(data.thickness_mils);
  let wftMicrons = toNumber(data.wft_microns);
  let dftMils = toNumber(data.dft_mils);
  let dftMicrons = toNumber(data.dft_microns);
  let costPerGal = toNumber(data.cost_per_gal_cdn);
  let costPerLitre = toNumber(data.cost_per_litre);

  if (preferredFor("wft") === "metric") {
    if (wftMicrons > 0) {
      wftMils = wftMicrons / MICRONS_PER_MIL;
    } else if (wftMils > 0) {
      wftMicrons = wftMils * MICRONS_PER_MIL;
    }
  } else {
    if (wftMils > 0) {
      wftMicrons = wftMils * MICRONS_PER_MIL;
    } else if (wftMicrons > 0) {
      wftMils = wftMicrons / MICRONS_PER_MIL;
    }
  }

  if (preferredFor("dft") === "metric") {
    if (dftMicrons > 0) {
      dftMils = dftMicrons / MICRONS_PER_MIL;
    } else if (dftMils > 0) {
      dftMicrons = dftMils * MICRONS_PER_MIL;
    }
  } else {
    if (dftMils > 0) {
      dftMicrons = dftMils * MICRONS_PER_MIL;
    } else if (dftMicrons > 0) {
      dftMils = dftMicrons / MICRONS_PER_MIL;
    }
  }

  if (preferredFor("coverage") === "metric") {
    if (theoreticalM2PerL > 0) {
      theoreticalFt2PerGal =
        theoreticalM2PerL * FT2_PER_M2 * LITRES_PER_US_GALLON;
    } else if (theoreticalFt2PerGal > 0) {
      theoreticalM2PerL =
        theoreticalFt2PerGal / FT2_PER_M2 / LITRES_PER_US_GALLON;
    }
  } else {
    if (theoreticalFt2PerGal > 0) {
      theoreticalM2PerL =
        theoreticalFt2PerGal / FT2_PER_M2 / LITRES_PER_US_GALLON;
    } else if (theoreticalM2PerL > 0) {
      theoreticalFt2PerGal =
        theoreticalM2PerL * FT2_PER_M2 * LITRES_PER_US_GALLON;
    }
  }

  if (preferredFor("cost") === "metric") {
    if (costPerLitre > 0) {
      costPerGal = costPerLitre * LITRES_PER_US_GALLON;
    } else if (costPerGal > 0) {
      costPerLitre = costPerGal / LITRES_PER_US_GALLON;
    }
  } else {
    if (costPerGal > 0) {
      costPerLitre = costPerGal / LITRES_PER_US_GALLON;
    } else if (costPerLitre > 0) {
      costPerGal = costPerLitre * LITRES_PER_US_GALLON;
    }
  }

  const appliedCoverageFt2PerGal =
    wftMils > 0 ? theoreticalFt2PerGal / wftMils : 0;
  const appliedCoverageM2PerL =
    appliedCoverageFt2PerGal > 0
      ? appliedCoverageFt2PerGal / FT2_PER_M2 / LITRES_PER_US_GALLON
      : 0;

  const paintCostPerFt2 =
    appliedCoverageFt2PerGal > 0 ? costPerGal / appliedCoverageFt2PerGal : 0;
  const paintCostPerM2 = paintCostPerFt2 * FT2_PER_M2;

  return {
    theoreticalFt2PerGal: roundValue(theoreticalFt2PerGal),
    theoreticalM2PerL: roundValue(theoreticalM2PerL),
    wftMils: roundValue(wftMils, 2),
    wftMicrons: roundValue(wftMicrons, 2),
    dftMils: roundValue(dftMils, 2),
    dftMicrons: roundValue(dftMicrons, 2),
    costPerGal: roundValue(costPerGal),
    costPerLitre: roundValue(costPerLitre),
    appliedCoverageFt2PerGal: roundValue(appliedCoverageFt2PerGal),
    appliedCoverageM2PerL: roundValue(appliedCoverageM2PerL),
    paintCostPerFt2: roundValue(paintCostPerFt2),
    paintCostPerM2: roundValue(paintCostPerM2),
  };
};

export const createDefaultCoatingFormData = (
  year,
  includeMaterialType = true,
) => ({
  ...(includeMaterialType ? { material_type: "" } : {}),
  ...(includeMaterialType ? { paint_id: "" } : {}),
  coat_type: "",
  material_name: "",
  input_uom_mode: "imperial",
  cost_per_unit_cad: "",
  cost_per_gal_cdn: "",
  cost_per_litre: "",
  thickness_mils: "",
  wft_microns: "",
  dft_mils: "",
  dft_microns: "",
  coverage_ft2_per_gal: "",
  coverage_25um_m2_per_l: "",
  coverage_field_mils: "",
  cost_per_unit_cdn: "",
  cost_per_m2_cdn: "",
  year,
  currency: "CAD",
  sort_order: 99,
  is_active: true,
});

export const buildCoatingPayload = (formData, derivedValues) => ({
  ...formData,
  coverage_25um_m2_per_l: derivedValues.theoreticalM2PerL,
  wft_microns: derivedValues.wftMicrons,
  dft_microns: derivedValues.dftMicrons,
  dft_mils: derivedValues.dftMils,
  cost_per_litre: derivedValues.costPerLitre,
  thickness_mils: derivedValues.wftMils,
  coverage_ft2_per_gal: derivedValues.theoreticalFt2PerGal,
  coverage_field_mils: derivedValues.appliedCoverageFt2PerGal,
  cost_per_gal_cdn: derivedValues.costPerGal,
  cost_per_unit_cad: derivedValues.paintCostPerM2,
  cost_per_m2_cdn: derivedValues.paintCostPerM2,
  cost_per_unit_cdn: derivedValues.paintCostPerFt2,
});
